import {Pagination as BsPagination} from 'react-bootstrap'
import {ChevronLeft, ChevronRight} from 'react-bootstrap-icons'
import {useTranslation} from 'react-i18next'
import styles from './Pagination.module.css'

interface PaginationProps {
    currentPage: number
    totalPages: number
    onPageChange: (page: number) => void
    className?: string
}

const SIBLING_COUNT = 1
const ALWAYS_SHOW_ALL_MAX = 9

type PageItem = number | 'ellipsis'

const buildPageItems = (current: number, total: number): PageItem[] => {
    if (total <= ALWAYS_SHOW_ALL_MAX) {
        return Array.from({length: total}, (_, i) => i)
    }

    const pages = new Set<number>([0, total - 1])
    for (let i = current - SIBLING_COUNT; i <= current + SIBLING_COUNT; i++) {
        if (i >= 0 && i < total) {
            pages.add(i)
        }
    }

    const sorted = [...pages].sort((a, b) => a - b)
    const items: PageItem[] = []
    let previous: number | null = null
    for (const page of sorted) {
        if (previous !== null && page - previous > 1) {
            items.push('ellipsis')
        }
        items.push(page)
        previous = page
    }
    return items
}

const Pagination = ({currentPage, totalPages, onPageChange, className}: PaginationProps) => {
    const {t} = useTranslation()

    if (totalPages <= 1) {
        return null
    }

    const items = buildPageItems(currentPage, totalPages)
    const isFirst = currentPage <= 0
    const isLast = currentPage >= totalPages - 1

    return (
        <div className={`${styles.wrapper}${className ? ` ${className}` : ''}`}>
            <BsPagination className={styles.pagination}>
                <BsPagination.Prev
                    aria-label={t('common:actions.previous')}
                    disabled={isFirst}
                    onClick={() => !isFirst && onPageChange(currentPage - 1)}
                >
                    <ChevronLeft aria-hidden="true" />
                </BsPagination.Prev>
                {items.map((item, index) =>
                    item === 'ellipsis' ? (
                        <BsPagination.Ellipsis key={`ellipsis-${index}`} disabled />
                    ) : (
                        <BsPagination.Item
                            key={item}
                            active={item === currentPage}
                            onClick={() => onPageChange(item)}
                        >
                            {item + 1}
                        </BsPagination.Item>
                    )
                )}
                <BsPagination.Next
                    aria-label={t('common:actions.next')}
                    disabled={isLast}
                    onClick={() => !isLast && onPageChange(currentPage + 1)}
                >
                    <ChevronRight aria-hidden="true" />
                </BsPagination.Next>
            </BsPagination>
        </div>
    )
}

export default Pagination
